// src/components/Game.tsx
import React, { useEffect, useRef, useState } from "react";
import Phaser from "phaser";
import { gameConfig } from "../game/config";
import { MobileControls } from "./MobileControls";
import styled from "styled-components";

const GameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: "Arial", sans-serif;
  position: relative;
  overflow: hidden;
`;

const GameWrapper = styled.div`
  border: 4px solid #333;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  background: #000;
  overflow: hidden;
  position: relative;

  /* Responsive sizing */
  width: min(90vw, 800px);
  height: min(67.5vw, 600px); /* Maintain 4:3 aspect ratio */

  @media (max-width: 768px) {
    width: 100vw;
    height: 75vw;
    border: none;
    border-radius: 0;
    max-height: 100vh;
  }
`;

const GameInfo = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 16px;
  margin-top: 16px;
  border-radius: 8px;
  max-width: 800px;
  text-align: center;

  @media (max-width: 768px) {
    display: none; /* Hide on mobile to save space */
  }
`;

const StatusIndicator = styled.div<{ isLoaded: boolean }>`
  background: ${(props) => (props.isLoaded ? "#4CAF50" : "#ff9800")};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 16px;
  display: inline-block;

  @media (max-width: 768px) {
    position: fixed;
    top: 10px;
    left: 10px;
    z-index: 1000;
    margin: 0;
    padding: 4px 12px;
    font-size: 12px;
  }
`;

const Game: React.FC = () => {
  const gameRef = useRef<HTMLDivElement>(null);
  const phaserGameRef = useRef<Phaser.Game | null>(null);
  const [isGameLoaded, setIsGameLoaded] = useState(false);
  const [gameError, setGameError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isInGameScene, setIsInGameScene] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const mobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) || window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const initializeGame = async () => {
      if (gameRef.current && !phaserGameRef.current) {
        try {
          // Set the parent element for Phaser
          const config = {
            ...gameConfig,
            parent: gameRef.current,
          };

          // Create the Phaser game instance
          phaserGameRef.current = new Phaser.Game(config);

          // Wait for the game to be ready
          phaserGameRef.current.events.once("ready", () => {
            setIsGameLoaded(true);
            console.log("✅ Phaser game loaded successfully!");

            // Listen for scene changes to show/hide mobile controls
            // Set initial scene state
            const currentScene =
              phaserGameRef.current!.scene.getScenes(true)[0];
            if (currentScene) {
              console.log("Initial scene:", currentScene.scene.key);
              const isGameScene = currentScene.scene.key === "GameScene";
              console.log("Is GameScene:", isGameScene);
              setIsInGameScene(isGameScene);
            }

            // Listen for scene transitions
            phaserGameRef.current!.events.on("step", () => {
              const activeScene =
                phaserGameRef.current!.scene.getScenes(true)[0];
              if (activeScene) {
                const isGameScene = activeScene.scene.key === "GameScene";
                setIsInGameScene(isGameScene);
              }
            });
          });
        } catch (error) {
          console.error("❌ Failed to initialize Phaser game:", error);
          setGameError(
            error instanceof Error ? error.message : "Unknown error"
          );
        }
      }
    };

    initializeGame();

    // Cleanup function
    return () => {
      if (phaserGameRef.current) {
        console.log("🧹 Cleaning up Phaser game...");
        phaserGameRef.current.destroy(true);
        phaserGameRef.current = null;
        setIsGameLoaded(false);
      }
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (phaserGameRef.current) {
        phaserGameRef.current.scale.refresh();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle mobile input
  const handleMobileInput = (
    player: 1 | 2,
    action: "left" | "right" | "jump",
    pressed: boolean
  ) => {
    if (phaserGameRef.current) {
      const gameScene = phaserGameRef.current.scene.getScene("GameScene");
      if (gameScene && gameScene.registry) {
        gameScene.registry.events.emit("mobile-input", {
          player,
          action,
          pressed,
        });
      }
    }
  };

  // Handle menu button press
  const handleMenuPress = () => {
    if (phaserGameRef.current) {
      const currentScene = phaserGameRef.current.scene.getScenes(true)[0];
      if (currentScene) {
        if (currentScene.scene.key === "GameScene") {
          currentScene.scene.start("MenuScene");
        } else {
          // If in menu, maybe show settings or do nothing
        }
      }
    }
  };

  if (gameError) {
    return (
      <GameContainer>
        <div
          style={{
            background: "#ff4444",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <h2>Game Error</h2>
          <p>{gameError}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "white",
              color: "#ff4444",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
              marginTop: "10px",
            }}
          >
            Reload Page
          </button>
        </div>
      </GameContainer>
    );
  }

  return (
    <GameContainer>
      <StatusIndicator isLoaded={isGameLoaded}>
        {isGameLoaded ? "🎮 Game Ready" : "⏳ Loading Game..."}
      </StatusIndicator>

      <GameWrapper>
        <div
          ref={gameRef}
          style={{
            width: "100%",
            height: "100%",
            display: isGameLoaded ? "block" : "flex",
            alignItems: "center",
            justifyContent: "center",
            background: isGameLoaded ? "transparent" : "#333",
          }}
        >
          {!isGameLoaded && (
            <div style={{ color: "white", textAlign: "center" }}>
              <div style={{ fontSize: "24px", marginBottom: "10px" }}>🎮</div>
              <div>Initializing Phaser...</div>
            </div>
          )}
        </div>
      </GameWrapper>

      {/* Mobile Controls */}
      {isMobile && isGameLoaded && (
        <MobileControls
          onPlayerInput={handleMobileInput}
          onMenuPress={handleMenuPress}
          isVisible={true} // Always show when mobile and game loaded
        />
      )}

      {/* Debug info for mobile */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            top: "60px",
            left: "10px",
            background: "rgba(0,0,0,0.8)",
            color: "white",
            padding: "5px",
            fontSize: "12px",
            zIndex: 2000,
          }}
        >
          Mobile: {isMobile.toString()} | GameLoaded: {isGameLoaded.toString()}{" "}
          | InGameScene: {isInGameScene.toString()}
        </div>
      )}

      {/* Desktop Game Info */}
      {isGameLoaded && !isMobile && (
        <GameInfo>
          <h3 style={{ margin: "0 0 12px 0" }}>
            🎯 Phase 1: Basic Game Foundation
          </h3>
          <p style={{ margin: "0 0 8px 0" }}>
            <strong>Player 1 (Blue):</strong> Arrow Keys to move and jump
            <br />
            <strong>Player 2 (Red):</strong> WASD keys to move and jump
          </p>
          <p style={{ margin: "0", fontSize: "14px", opacity: "0.8" }}>
            Press ESC to return to menu • Press R to restart • Players can push
            each other!
          </p>
        </GameInfo>
      )}
    </GameContainer>
  );
};

export default Game;
